/**
 * Fluent UI v9 theme for University of Utah Health (external-intake).
 *
 * Ported close to verbatim from aws-ai-monitoring-app/frontend/src/theme/utahHealthTheme.ts.
 *
 * Built from a Utah-red brand ramp anchored on the existing design tokens in
 * `src/styles/_variables.scss`:
 *   primary  #BE0000 (utah-red)
 *   hover    #A00000 (utah-red-dark)
 *   active   #890000 (utah-red-darker / red-rocks)
 *
 * `createLightTheme` picks brand tokens by ramp index, and that mapping is
 * opinionated, so we override the brand background triad explicitly to guarantee
 * the exact hover/pressed colors regardless of which index Fluent selects.
 */
import {
  createDarkTheme,
  createLightTheme,
  type Theme,
} from '@fluentui/react-components';
import { utahRedRamp as utahRed } from '@/shared/styles/colors';

// Brand font stack — exported so global element styles can match the Fluent
// theme's fontFamilyBase exactly.
export const FONT_FAMILY =
  "'Sofia Pro', 'Century Gothic', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif";

// Pin the brand background triad so primary buttons match the brand exactly.
const brandPins = {
  colorBrandBackground: '#BE0000',
  colorBrandBackgroundHover: '#A00000',
  colorBrandBackgroundPressed: '#890000',
  colorCompoundBrandForeground1: '#BE0000',
  colorCompoundBrandForeground1Hover: '#A00000',
  colorCompoundBrandForeground1Pressed: '#890000',
  colorCompoundBrandStroke: '#BE0000',
  colorCompoundBrandStrokeHover: '#A00000',
} as const;

export const utahHealthTheme: Theme = {
  ...createLightTheme(utahRed),
  fontFamilyBase: FONT_FAMILY,
  ...brandPins,
  // Brand page background (matches the legacy $color-background) so surfaces
  // that use the page-background token blend exactly.
  colorNeutralBackground3: '#F5F7F7',
};

export const utahHealthDarkTheme: Theme = {
  ...createDarkTheme(utahRed),
  fontFamilyBase: FONT_FAMILY,
  ...brandPins,
};

export default utahHealthTheme;
